<script setup>
import { ref, defineEmits, defineProps, computed } from 'vue'

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  value: {
    type: [String, Number],
    default: ''
  },
  icon: {
    type: [String, Array],
    default: null
  },
  iconClass: {
    type: String,
    default: ''
  },
  editable: {
    type: Boolean,
    default: false
  },
  tooltipTitle: {
    type: String,
    default: ''
  },
  tooltipText: {
    type: String,
    default: ''
  },
  tooltipIcon: {
    type: [String, Array],
    default: () => ['fas', 'edit']
  }
})

const emit = defineEmits(['click'])

const tooltipMinWidth = 220
const tooltipGap = 8

const valueEl = ref(null)

const isMenuOpen = ref(false)
const menuX = ref(0)
const menuY = ref(0)
const arrowX = ref(tooltipMinWidth / 2)

const displayValue = computed(() => {
  return props.value || '-'
})

const openMenu = (event) => {
  if (!props.editable) return

  const activatorRect = event.currentTarget.getBoundingClientRect()
  const valueRect = valueEl.value?.getBoundingClientRect()

  const targetRect = valueRect ?? activatorRect
  const { left, width } = targetRect
  const targetCenter = left + width / 2

  setMenuPosition(targetRect, tooltipMinWidth, targetCenter)
  isMenuOpen.value = true
}

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max)
}

const setMenuPosition = (targetRect, tooltipWidth, arrowTargetCenter) => {
  const idealLeft = arrowTargetCenter - tooltipWidth / 2
  const safeLeft = clamp(
    idealLeft,
    tooltipGap,
    window.innerWidth - tooltipWidth - tooltipGap
  )

  menuX.value = safeLeft
  menuY.value = targetRect.top - tooltipGap
  arrowX.value = arrowTargetCenter - safeLeft
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const handleClick = () => {
  if (!props.editable) return

  isMenuOpen.value = false
  emit('click')
}
</script>

<template>
  <div
    class="d-inline-flex align-center base-info-line"
    :class="{ 'editable-display': editable }"
    :role="editable ? 'button' : undefined"
    :tabindex="editable ? 0 : undefined"
    @mouseenter="openMenu"
    @mouseleave="closeMenu"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <font-awesome-icon
      v-if="icon"
      :icon="icon"
      :class="iconClass"
      class="mr-1"
    />

    <span class="font-weight-bold grey--text text--darken-1 mr-2">
      {{ label }}:
    </span>

    <span ref="valueEl" class="base-info-value">
      <slot>{{ displayValue }}</slot>
    </span>

    <v-menu
      v-if="editable"
      v-model="isMenuOpen"
      absolute
      :position-x="menuX"
      :position-y="menuY"
      :min-width="tooltipMinWidth"
      :close-on-content-click="false"
      content-class="base-info-tooltip-menu"
    >
      <div
        class="custom-tooltip-card"
        :style="{ '--tooltip-arrow-x': `${arrowX}px` }"
      >
        <div v-if="tooltipTitle" class="custom-tooltip-title py-1 px-4">
          <font-awesome-icon
            v-if="tooltipIcon"
            :icon="tooltipIcon"
            class="mr-2"
          />
          {{ tooltipTitle }}
        </div>

        <div class="custom-tooltip-body py-2 px-4 text-body-2">
          {{ tooltipText }}
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style scoped>
.base-info-line {
  line-height: 1.2;
  width: fit-content;
}

.editable-display {
  cursor: pointer;
}

.editable-display:hover .base-info-value {
  font-weight: 700;
  text-decoration: underline;
}
</style>

<style>
.base-info-tooltip-menu {
  contain: none !important;
  overflow: visible !important;
  box-shadow: none !important;
  background: transparent !important;
  transform: translateY(-100%) !important;
}

.custom-tooltip-card {
  position: relative;
  min-width: 220px;
  width: max-content;
  background: #fff;
  color: #333;
  border: 1px solid #cfcfcf;
  border-radius: 3px;
  overflow: visible;
}

.custom-tooltip-card::before {
  content: '';
  position: absolute;
  left: var(--tooltip-arrow-x);
  bottom: -8px;
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #cfcfcf;
}

.custom-tooltip-card::after {
  content: '';
  position: absolute;
  left: var(--tooltip-arrow-x);
  bottom: -7px;
  transform: translateX(-50%);
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid #fff;
}

.custom-tooltip-title {
  font-weight: 700;
  border-bottom: 1px solid #dcdcdc;
  background: #e9e7e7;
}

.custom-tooltip-body {
  color: #333;
  background: #fff;
}
</style>
